import { StoryblokVue, apiPlugin, type ISbStoryParams } from '@storyblok/vue'
import { defineNuxtPlugin, useRuntimeConfig } from '#app'
import type { InjectionKey } from 'vue'

// The storyblok plugin doesn't offer its own proper type for the vue plugin.
interface StoryblokPluginSettings {
  accessToken: string
  bridge?: boolean
  useApiClient?: boolean
  version?: 'draft' | 'published'
}

export type StoryblokSettings = Required<StoryblokPluginSettings> & {
  apiSettings: Partial<ISbStoryParams>
}

export const storyblokSettingsKey = Symbol() as InjectionKey<StoryblokSettings>

export default defineNuxtPlugin({
  name: 'storyblok',
  setup({ vueApp }) {
    const { storyblok } = useRuntimeConfig().public
    const { environment, host, path } = usePageEnvironment()
    const config = getPageConfig(environment, host, path)
    if (config === undefined) {
      // some pages like health check don't need any storyblok and should be callable as any IP or domain.
      return
    }

    let token: string
    if (import.meta.server) {
      token = useRuntimeConfig()[config.storyblok.tokenNuxtRuntimeName] as string
    } else {
      // storyblokAccessToken gets written by the storyblok nitro plugin.
      token = useRuntimeConfig().public.storyblokAccessToken
    }

    const params: StoryblokPluginSettings = {
      ...storyblok,
      accessToken: token,
    }
    if (config.storyblok.accessLevel === 'draft') {
      params.bridge = true
      params.useApiClient = true
      params.version = 'draft'
    } else {
      params.bridge = false
      params.useApiClient = false
      params.version = 'published'
    }

    vueApp.provide(storyblokSettingsKey, {
      ...params as Required<StoryblokPluginSettings>,
      apiSettings: {
        version: params.version,
        token: params.accessToken,
      },
    })
    vueApp.use(StoryblokVue, { ...params, use: [apiPlugin] })
  },
})
